import { useReducer, useEffect } from 'react';
import { useSyncExternalStore } from 'use-sync-external-store/shim';
import type { StoreBehavior } from '../types.tsx';
/**
 * functionality is inspired by https://github.com/pmndrs/jotai/blob/main/src/react/useAtomValue.ts
 * the core idea is to "sync" external data via useReducer that is "concurrent" friendly
 * - all "new" connections are reading from "current store state"
 * - all "existing" connections are scheduling updates to later
 */
const useConcurrentExternalStore = <T,>(
	subscribe: (onStoreChange: () => void) => () => void,

	getSnapshot: () => T,
) => {
	const [valueFromReducer, rerender] = useReducer(
		(prev) => {
			const nextValue = getSnapshot();
			if (Object.is(prev, nextValue)) {
				return prev;
			}
			return nextValue;
		},
		undefined,
		() => getSnapshot(),
	);

	const value = valueFromReducer;

	useEffect(() => {
		return subscribe(rerender);
	}, [subscribe]);

	return value;
};

export const useStoreState = <T = unknown,>(
	subscribe: (onStoreChange: () => void) => () => void,
	getSnapshot: () => T,
	behavior: StoreBehavior | undefined,
): T => {
	if (behavior === 'performance') {
		// oxlint-disable-next-line react-hooks/rules-of-hooks, react/react-compiler
		return useConcurrentExternalStore(subscribe, getSnapshot);
	}
	// default
	// oxlint-disable-next-line react-hooks/rules-of-hooks, react/react-compiler
	return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};
